import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useTicketDetail,
  useTicketMessages,
  useAddTicketMessage,
  useUserConfirmTicket,
  useDeleteTicket,
} from '@saiens/shared/hooks';
import type { TicketState, TicketPriority } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Paperclip,
  ExternalLink,
} from 'lucide-react';

const STATE_LABELS: Record<TicketState, string> = {
  draft: '草稿',
  submitted: '已送出',
  received: '已接收',
  ai_developing: 'AI開發中',
  testing: '已發佈測試',
  user_testing: '使用者確認',
  production: '已上傳正式',
  done: '完成',
};

const STATE_STEPS: TicketState[] = [
  'draft',
  'submitted',
  'received',
  'ai_developing',
  'testing',
  'user_testing',
  'production',
  'done',
];

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '緊急',
};

const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'text-green-600',
  medium: 'text-blue-600',
  high: 'text-orange-600',
  urgent: 'text-red-600',
};

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numericId = id ? parseInt(id, 10) : undefined;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useTicketDetail(numericId);
  const { data: messages } = useTicketMessages(numericId);
  const addMessageMutation = useAddTicketMessage();
  const userConfirmMutation = useUserConfirmTicket();
  const deleteMutation = useDeleteTicket();

  const [messageInput, setMessageInput] = useState('');

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim() || !numericId) return;
    await addMessageMutation.mutateAsync({
      ticketId: numericId,
      content: messageInput.trim(),
    });
    setMessageInput('');
  }, [messageInput, numericId, addMessageMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleUserConfirm = useCallback(
    async (confirmed: boolean) => {
      if (!numericId) return;
      await userConfirmMutation.mutateAsync({ id: numericId, confirmed });
    },
    [numericId, userConfirmMutation],
  );

  const handleDelete = useCallback(async () => {
    if (!numericId) return;
    await deleteMutation.mutateAsync(numericId);
    navigate('/ticket', { replace: true });
  }, [numericId, deleteMutation, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <p className="text-red-500">載入失敗</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 text-primary"
        >
          返回
        </button>
      </div>
    );
  }

  const currentStepIndex = STATE_STEPS.indexOf(data.state);
  const typeLabel = data.ticket_type === 'requirement' ? '系統需求單' : 'BUG處理單';
  const typeColor = data.ticket_type === 'requirement' ? 'text-indigo-600' : 'text-red-600';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 pb-32">
      {/* Header */}
      <div className="flex items-center justify-between bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{data.name}</h1>
            <span className={`text-xs font-medium ${typeColor}`}>{typeLabel}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.state === 'draft' && (
            <>
              <Link
                to={`/ticket/${data.id}/edit`}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
              >
                <Edit3 className="h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="rounded-lg p-2 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Stepper */}
      <div className="overflow-x-auto bg-white px-4 py-3 shadow-sm">
        <div className="flex min-w-max items-center gap-1">
          {STATE_STEPS.map((step, idx) => {
            const isActive = idx <= currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            return (
              <div key={step} className="flex items-center">
                {idx > 0 && (
                  <div
                    className={`h-0.5 w-4 ${
                      idx <= currentStepIndex ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                )}
                <div
                  className={`rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap ${
                    isCurrent
                      ? 'bg-primary text-white'
                      : isActive
                        ? 'bg-primary/20 text-primary'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {STATE_LABELS[step]}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info Cards */}
      <div className="space-y-3 p-4">
        {/* Basic Info */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">基本資訊</h2>
          <div className="space-y-2">
            <InfoRow label="系統別" value={data.system_name || '-'} />
            <InfoRow label="功能" value={data.function_name || '-'} />
            <InfoRow
              label="優先級"
              value={PRIORITY_LABELS[data.priority]}
              valueClass={PRIORITY_COLORS[data.priority]}
            />
            <InfoRow label="提報人" value={data.employee_name || '-'} />
            <InfoRow
              label="期望完成日"
              value={data.expected_date ? new Date(data.expected_date).toLocaleDateString('zh-TW') : '-'}
            />
            <InfoRow
              label="預計完成日"
              value={data.planned_date ? new Date(data.planned_date).toLocaleDateString('zh-TW') : '-'}
            />
            {data.completion > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">完成度</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${data.completion}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {data.completion}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {data.description && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">描述</h2>
            <p className="text-sm whitespace-pre-wrap text-gray-700">
              {data.description}
            </p>
          </div>
        )}

        {/* Attachments */}
        {data.attachments.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">
              附件 ({data.attachments.length})
            </h2>
            <div className="space-y-2">
              {data.attachments.map((att) => (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-gray-700">{att.name}</p>
                    <p className="text-xs text-gray-400">
                      {(att.file_size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">對話訊息</h2>
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {(!messages || messages.length === 0) ? (
              <p className="text-center text-sm text-gray-400">尚無訊息</p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.author_type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-xl px-3 py-2 ${
                      msg.author_type === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <p className="text-xs font-medium opacity-70">
                      {msg.author_name || (msg.author_type === 'user' ? '使用者' : '工程師')}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    {msg.create_date && (
                      <p className="mt-1 text-right text-xs opacity-50">
                        {new Date(msg.create_date).toLocaleString('zh-TW', {
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message input */}
          <div className="mt-3 flex items-end gap-2 border-t pt-3">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="輸入訊息..."
              rows={1}
              className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || addMessageMutation.isPending}
              className="rounded-lg bg-primary p-2 text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer actions - only for user_testing state */}
      {data.state === 'user_testing' && (
        <div className="safe-area-bottom fixed bottom-16 left-0 right-0 border-t border-gray-200 bg-white px-4 py-3">
          <p className="mb-2 text-center text-sm text-gray-600">
            請確認測試結果
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleUserConfirm(false)}
              disabled={userConfirmMutation.isPending}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-red-300 py-3 text-sm font-medium text-red-600 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" />
              測試未通過
            </button>
            <button
              type="button"
              onClick={() => handleUserConfirm(true)}
              disabled={userConfirmMutation.isPending}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              測試通過
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueClass = 'text-gray-700',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}
